import { sizeTemplates } from '../compile/from-base-static-registries.json';
import { readItemField } from '../compile/item-field-reader';
import { readInventoryField, readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { COIN_GP } from './unit-gp';
import type { Row } from '../spawn/row-plan';

const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const templates: Readonly<Record<string, Readonly<Record<string, string | null>>>> = sizeTemplates;

/** Raw registry selection shared by creation producers; Medium deliberately has no template. */
export function inventoryCreationSizeTemplate(type: string, sizeId: string): string {
    const bucket = ['armor', 'barding'].includes(type) ? 'armor' : ['weapon', 'natural_weapon'].includes(type) ? 'weapon' : 'equipment';
    return type === 'currency' ? '' : templates[upper(sizeId)]?.[bucket] ?? '';
}
export const inventoryCreationSizeTemplateIds = (): string[] => [...new Set(Object.values(templates)
    .flatMap(row => Object.values(row)).filter((id): id is string => !!id))];

/** A held object's own size never fits its contents. Only creature creation reads this policy. */
export function inventoryCreationHolderSize(holder: Row, value: (row: Row, key: string) => unknown): string {
    if (readInventoryField(holder, 'inventory_id')) return '';
    const creature = !!readItemField(holder.encounter_creatures as Row | null, 'encounter_creature_id')
        || !!readItemField(holder.player_characters as Row | null, 'item_id')
        || readItemField(holder, 'item_type') === 'creature';
    return creature ? upper(readLayerListEntryUuid(value(holder, 'size'))) : '';
}

/** Called once for a new row, before stack identity is chosen; movement never calls this. */
export function inventoryCreationSizingTemplate(definition: Row, holder: Row,
    value: (row: Row, key: string) => unknown, explicitSize?: string | null): string {
    if (explicitSize || COIN_GP[upper(readItemField(definition, 'item_id'))] !== undefined) return '';
    return inventoryCreationSizeTemplate(String(readItemField(definition, 'item_type') ?? ''), inventoryCreationHolderSize(holder, value));
}

/** Pending authoring rows supply the same ordered raw members as their eventual SELECT.
 * Only new definition relationships are added; held inventory/creature state is borrowed. */
export function inventoryCreationTemplateSources(base: Row, writes: readonly { table: string; row: Row }[]): Row {
    const templates = writes.filter(write => write.table === 'enchantment_templates').map(write => write.row);
    if (!templates.length) return base;
    const members = writes.filter(write => write.table === 'enchantment_template_enchantments').map((write, index) => ({
        ...write.row, enchantment_template_enchantment_i: index }));
    return { ...base, enchantment_templates: [...Array.isArray(base.enchantment_templates) ? base.enchantment_templates : [], ...templates],
        enchantment_template_enchantments: [...Array.isArray(base.enchantment_template_enchantments) ? base.enchantment_template_enchantments : [], ...members] };
}
